import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect } from "react";
import { useSite } from "@/contexts/SiteContext";
import { fileUrl } from "@/lib/api";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// Cover: explicit cover, else first gallery image.
function sessionCover(s) {
  if (s.cover) return s.cover;
  const g = Array.isArray(s.gallery) ? s.gallery : [];
  return g[0] || "";
}

export default function PreWedding() {
  useReveal();
  const { preweddingSessions } = useSite() || {};
  const sessions = (preweddingSessions || []).filter(s => (s.status || "published") !== "draft");
  const hero = sessions[0] ? sessionCover(sessions[0]) : "";

  return (
    <div data-testid="prewedding-page" className="bg-[#F5EFE4] text-[#111111] min-h-screen">

      {/* Full-screen hero */}
      <section className="cinematic-hero relative h-[72vh] md:h-[78vh] overflow-hidden" data-gsap="hero">
        {hero ? (
          <img src={fileUrl(hero)} alt="Pre-Wedding" loading="eager" data-gsap="hero-media"
            className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 32%", opacity: 1 }} />
        ) : (
          <div className="absolute inset-0 bg-[#DDD5C8]" />
        )}
        <div className="absolute inset-0 flex items-end z-10">
          <div className="max-w-[1380px] mx-auto w-full px-6 md:px-12 pb-16 md:pb-24 text-[#F5EFE4]">
            <span className="eyebrow text-[#E6C988] text-[0.6rem]" data-gsap="hero-kicker">Romance · Chemistry · Cinema</span>
            <h1 className="page-hero-title mt-3 font-display-italic" data-gsap="hero-title">Pre-Wedding.</h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-28 px-6 md:px-12" data-testid="prewedding-intro" data-gsap="reveal">
        <div className="max-w-3xl mx-auto text-center">
          <span className="gold-line gold-line-center block mb-8" />
          <p className="font-display-italic text-[rgba(17,17,17,0.82)]"
            style={{ fontSize: "clamp(1.3rem,2.5vw,1.9rem)", lineHeight: 1.55 }}>
            Pre-wedding stories are about chemistry, comfort, and connection. Each session below is its
            own little film — open one to wander through the full gallery.
          </p>
        </div>
      </section>

      {/* Session cards — airy, romantic, offset editorial rhythm */}
      <section className="pb-24 md:pb-32 px-6 md:px-12" data-testid="prewedding-sessions" data-gsap="reveal">
        <div className="max-w-[1380px] mx-auto">
          {sessions.length === 0 ? (
            <div className="text-center py-20">
              <p className="eyebrow text-[11px] text-[rgba(17,17,17,0.4)]">
                Pre-wedding sessions are being curated. Please check back soon.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16 md:gap-y-24" data-gsap="stagger">
              {sessions.map((s, i) => (
                <Link
                  key={s.slug || i}
                  to={`/pre-wedding/${s.slug}`}
                  data-testid={`prewedding-card-${s.slug}`}
                  className={`group block reveal ${i % 2 === 1 ? "md:mt-24" : ""}`}
                  style={{ transitionDelay: `${(i % 3) * 90}ms` }}
                >
                  <div className="frame bg-[#DDD5C8] overflow-hidden gsap-image-hover" data-gsap="image" style={{ aspectRatio: "4/5", borderRadius: "2px" }}>
                    <img
                      src={fileUrl(sessionCover(s))}
                      alt={s.couple}
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: "100%", height: "100%", objectFit: s.coverFit === "contain" ? "contain" : "cover",
                        objectPosition: s.coverPositionDesktop && s.coverPositionDesktop !== "center center"
                          ? s.coverPositionDesktop : "center 30%",
                      }}
                    />
                  </div>
                  <div className="mt-6 flex items-start justify-between gap-6">
                    <div>
                      <div className="eyebrow text-[10px] text-[#B8914A]">
                        {[s.type, s.location].filter(Boolean).join(" · ")}
                        {s.date ? ` · ${s.date}` : ""}
                      </div>
                      <h3 className="font-display-italic text-3xl md:text-4xl mt-2 group-hover:text-[#B8914A] transition">
                        {s.couple}
                      </h3>
                      {s.story && (
                        <p className="text-sm text-[rgba(17,17,17,0.65)] mt-3 max-w-md leading-relaxed">{s.story}</p>
                      )}
                    </div>
                    <div className="eyebrow text-[10px] flex items-center gap-2 group-hover:text-[#B8914A] mt-3 shrink-0">
                      View Album <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 text-center bg-[#111111] text-[#F5EFE4]" data-gsap="reveal">
        <h2 className="font-display" style={{ fontSize: "clamp(2.2rem,4vw,3.8rem)" }}>
          Book Your Pre-Wedding
        </h2>
        <p className="mt-3 text-[0.83rem] text-[rgba(245,239,228,0.55)]">
          From quiet city corners to dreamy destinations, we shoot it like cinema.
        </p>
        <Link to="/contact" className="btn-gold-fill mt-10 inline-flex" data-testid="prewedding-cta">
          Enquire Now <ArrowRight size={13} />
        </Link>
      </section>
    </div>
  );
}
